import React, { useMemo, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, Tab } from '@mui/material';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
  INSCRICOES_POR_TORNEIO,
  InscricoesPorTorneioData,
  InscricoesPorTorneioVars,
} from '../../services/torneioService';
import { buscarFilasEsperaPorTorneio, promoverFilaEspera, FilaEspera } from '../../services/filaEsperaService';
import { dispatchToast } from '../../context/ToastContext';
import { useProfile } from '../../context/ProfileContext';
import { useLigaPermissions } from '../../hooks/useLigaPermissions';
import TournamentHeader from '../../components/liga/TournamentHeader';
import TournamentInfoTab from './tournamentManagement/TournamentInfoTab';
import TournamentRegistrationsTab from './tournamentManagement/TournamentRegistrationsTab';
import TournamentMatchesTab from './tournamentManagement/TournamentMatchesTab';
import TournamentHistoryTab from './tournamentManagement/TournamentHistoryTab';
import TournamentFinanceiroTab from './tournamentManagement/TournamentFinanceiroTab';
import { PageBreadcrumb } from '../../components/navigation';

const LeagueTournamentManagement: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentProfile, isLoading: profileLoading } = useProfile();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;

  const { canManageTournaments, canManagePayments, isLoading: permissionsLoading } = useLigaPermissions(ligaId);

  const [tabValue, setTabValue] = React.useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  });

  // Query para buscar dados do torneio
  const { data: torneioData, loading: loadingTorneio, error: errorTorneio, refetch: refetchTorneio } = useQuery<
    TorneioPorIdData,
    TorneioPorIdVars
  >(TORNEIO_POR_ID, {
    variables: { id: id! },
    skip: !id,
    fetchPolicy: 'no-cache',
  });

  // Query para buscar inscrições
  const { data: inscricoesData, loading: loadingInscricoes, refetch: refetchInscricoes } = useQuery<
    InscricoesPorTorneioData,
    InscricoesPorTorneioVars
  >(INSCRICOES_POR_TORNEIO, {
    variables: { torneioId: id! },
    skip: !id,
    fetchPolicy: 'no-cache',
  });

  // Buscar filas de espera usando o service
  const [filasEspera, setFilasEspera] = useState<FilaEspera[]>([]);
  const [loadingFilas, setLoadingFilas] = useState(false);
  const [errorFilas, setErrorFilas] = useState<Error | null>(null);
  const [promotingFila, setPromotingFila] = useState(false);

  useEffect(() => {
    if (!id) return;
    
    const fetchFilas = async () => {
      setLoadingFilas(true);
      try {
        const filas = await buscarFilasEsperaPorTorneio(id);
        setFilasEspera(filas);
        setErrorFilas(null);
      } catch (error) {
        setErrorFilas(error as Error);
      } finally {
        setLoadingFilas(false);
      }
    };

    fetchFilas();
  }, [id]);

  const torneio = torneioData?.torneioPorId;
  const categorias = torneio?.categoriasTorneio || [];
  const inscricoes = inscricoesData?.inscricoesTorneioPorAtletaIdTorneioId || [];

  // Cálculo de estatísticas de categorias
  const statsCategorias = useMemo(() => {
    const totalCategorias = categorias.length;
    const categoriasSimples = categorias.filter((c) => c.numeroAtletas === 1).length;
    const categoriasDuplas = categorias.filter((c) => c.numeroAtletas === 2).length;
    return { totalCategorias, categoriasSimples, categoriasDuplas };
  }, [categorias]);

  // Cálculo de estatísticas de inscrições
  const statsInscricoes = useMemo(() => {
    const totalInscricoes = inscricoes.length;
    const inscricoesPendentes = inscricoes.filter(
      (i) => i.status !== 'CONFIRMADA'
    ).length;
    const inscricoesConfirmadas = inscricoes.filter(
      (i) => i.status === 'CONFIRMADA'
    ).length;
    return { totalInscricoes, inscricoesPendentes, inscricoesConfirmadas };
  }, [inscricoes]);

  // Cálculo de fila de espera e vagas (agregado no servidor por categoria, categoria.ocupacao)
  const statsFilaEspera = useMemo(() => {
    let inscricoesFilaEspera = 0;
    let vagasDisponiveis = 0;
    categorias.forEach((categoria) => {
      inscricoesFilaEspera += categoria.ocupacao?.filaEspera ?? 0;
      vagasDisponiveis += categoria.ocupacao?.vagasDisponiveis ?? 0;
    });

    return { inscricoesFilaEspera, vagasDisponiveis };
  }, [categorias]);

  // Cálculo de estatísticas de partidas
  const statsPartidas = useMemo(() => {
    const todasPartidas = categorias.flatMap(
      (c) => c.fasesCategoria?.flatMap((f) => f.partidas || []) || []
    );
    const totalPartidas = todasPartidas.length;
    // Verificar se partida foi realizada: placar existe e tem resultado final
    // Prioriza verificação por resultadoFinalA e resultadoFinalB, fallback para placar.id
    const partidasRealizadas = todasPartidas.filter((p) => {
      if (!p.placar) return false;
      const placar = p.placar;
      // Se tem resultadoFinalA e resultadoFinalB, partida foi realizada
      if (
        placar.resultadoFinalA != null &&
        placar.resultadoFinalB != null
      ) {
        return true;
      }
      // Fallback: se tem id do placar, considera realizada
      return placar.id != null;
    }).length;
    const partidasPendentes = totalPartidas - partidasRealizadas;
    return { totalPartidas, partidasRealizadas, partidasPendentes };
  }, [categorias]);

  const handlePromoverFilaEsperaPorCategoria = async (categoriaId: string) => {
    setPromotingFila(true);
    try {
      await promoverFilaEspera({ categoriaTorneioId: categoriaId });
      dispatchToast('Inscrição promovida da fila de espera!', 'success');
      await refetchInscricoes();
    } catch (error) {
      console.error('Erro ao promover fila de espera:', error);
      dispatchToast('Erro ao promover inscrição da fila de espera.', 'error');
    } finally {
      setPromotingFila(false);
    }
  };

  const handlePromoverFilaEsperaPorId = async (filaEsperaId: string) => {
    setPromotingFila(true);
    try {
      await promoverFilaEspera({ filaEsperaId });
      dispatchToast('Inscrição promovida da fila de espera!', 'success');
      await refetchInscricoes();
    } catch (error) {
      console.error('Erro ao promover fila de espera:', error);
      dispatchToast('Erro ao promover inscrição da fila de espera.', 'error');
    } finally {
      setPromotingFila(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    navigate(`/liga/tournament/${id}/edit`);
  };

  // Se ainda está carregando o perfil, mostra loading
  if (profileLoading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  // Se não for perfil de liga, não renderiza nada
  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  // Verificar permissão de gerenciamento de torneios
  if (!canManageTournaments) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-warning" role="alert">
                        <h5 className="alert-heading">Acesso Negado</h5>
                        <p className="mb-0">
                          Você não tem permissão para gerenciar torneios desta Liga. Apenas
                          administradores e gerentes podem realizar esta ação.
                        </p>
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/dashboard')}
                      >
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  if (loadingTorneio || loadingInscricoes || loadingFilas) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  // Tratamento de erro para filas de espera (não crítico, pode continuar sem dados)
  if (errorFilas) {
    console.warn('Erro ao carregar filas de espera:', errorFilas);
  }

  if (errorTorneio || !torneio) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        Erro ao carregar torneio. Tente novamente.
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/tournaments')}
                      >
                        Voltar aos Torneios
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Appfooter />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="mb-3">
                  <PageBreadcrumb
                    items={[
                      { label: 'Dashboard', href: '/liga/dashboard' },
                      { label: 'Torneios', href: '/liga/tournaments' },
                      { label: torneio?.nome ?? 'Torneio' },
                    ]}
                    onBack={() => navigate('/liga/tournaments')}
                    variant="bootstrap"
                  />
                </div>
                {/* Cabeçalho do Torneio */}
                <TournamentHeader torneio={torneio} onEdit={handleEdit} />

                {/* Sistema de Abas */}
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                  <div className="card-body p-4">
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      aria-label="abas do torneio"
                      indicatorColor="primary"
                      textColor="primary"
                    >
                      <Tab label="Informações" />
                      <Tab label="Inscrições" />
                      <Tab label="Partidas" />
                      <Tab label="Histórico" />
                      <Tab label="Financeiro" />
                    </Tabs>

                    {/* Conteúdo das Abas */}
                    {tabValue === 0 && (
                      <TournamentInfoTab
                        torneioId={id!}
                        torneio={torneio}
                        categorias={categorias}
                        inscricoes={inscricoes}
                        statsCategorias={statsCategorias}
                        statsInscricoes={statsInscricoes}
                        statsFilaEspera={statsFilaEspera}
                        statsPartidas={statsPartidas}
                        navigate={navigate}
                        onRefresh={refetchTorneio}
                      />
                    )}

                    {tabValue === 1 && (
                      <TournamentRegistrationsTab
                        torneioId={id!}
                        torneio={torneio}
                        inscricoes={inscricoes}
                        categorias={categorias}
                        onPromoverFilaEsperaPorCategoria={handlePromoverFilaEsperaPorCategoria}
                        onPromoverFilaEsperaPorId={handlePromoverFilaEsperaPorId}
                        promotingFila={promotingFila}
                      />
                    )}

                    {tabValue === 2 && (
                      <TournamentMatchesTab
                        torneioId={id!}
                        categorias={categorias}
                        canEdit={canManageTournaments}
                      />
                    )}

                    {tabValue === 3 && (
                      <TournamentHistoryTab
                        torneioId={id!}
                        torneio={torneio}
                      />
                    )}

                    {tabValue === 4 && (
                      <TournamentFinanceiroTab
                        torneioId={id!}
                        categorias={categorias}
                        inscricoes={inscricoes}
                        ligaId={ligaId!}
                        canManagePayments={canManagePayments}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Appfooter />
    </>
  );
};

export default LeagueTournamentManagement;

